import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { Input } from "@/components/core/forms/input";
import Pagination from "@/components/core/pagination";
import { Content } from "@/components/layouts/content";
import { CreateProjectModal } from "@/components/projects/create-project-modal";
import { EditProjectModal } from "@/components/projects/edit-project-modal";
import { groupedProjectsQueryOptions, projectsQueryOptions, useProjectDeleteMutation } from "@/features/dashboard/api/queries/project-query";
import type { Project } from "@/features/dashboard/types/project";
import { queryClient } from "@/query-client";
import { exportToCSV, exportToPDF, exportToXLSX, prepareProjectDataForExport } from "@/utils/export-data";
import { formatDate } from "@/utils/format-date";
import {
	IconBuilding,
	IconCategory,
	IconFileExport,
	IconFileSpreadsheet,
	IconFileTypeCsv,
	IconFileTypePdf,
	IconPlus,
	IconSearch,
	IconUsers,
	IconX,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

const columns: ColumnDef<Project>[] = [
	{
		id: "name",
		label: "Project Name",
		cellClassName: "min-w-[200px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => {
			const categoryColors = {
				"one-time": "bg-blue-100 text-blue-800",
				recurring: "bg-green-100 text-green-800",
				"recurring-one-time": "bg-purple-100 text-purple-800",
			};
			const categoryLabels = {
				"one-time": "One-time",
				recurring: "Recurring",
				"recurring-one-time": "Recurring & One-time",
			};

			return (
				<Link to={`/projects/${data.id}`} className="flex flex-col font-semibold">
					<span className="text-blue-600">{data.name}</span>
					<span className="text-xs text-gray-500">{data.client ? data.client.company || data.client.name : "-"}</span>
					<span
						className={`px-2 py-1 ${data.category === "recurring-one-time" ? "w-30" : "w-16"} rounded-full text-xs font-medium ${categoryColors[data.category]}`}
					>
						{categoryLabels[data.category]}
					</span>
				</Link>
			);
		},
	},
	{
		id: "description",
		label: "Description",
		cellClassName: "min-w-[200px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.description || "-"}</span>,
	},
	{
		id: "date_start",
		label: "Start Date",
		cellClassName: "min-w-[120px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.date_start ? new Date(data.date_start).toLocaleDateString() : "-"}</span>,
	},
	{
		id: "ntp_date",
		label: "NTP Date",
		cellClassName: "min-w-[120px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.ntp_date ? formatDate(data.ntp_date, "PP") : "-"}</span>,
	},
	{
		id: "noa_date",
		label: "NOA Date",
		cellClassName: "min-w-[120px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.noa_date ? formatDate(data.noa_date, "PP") : "-"}</span>,
	},
	{
		id: "contract_date",
		label: "Contract Date",
		cellClassName: "min-w-[120px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.contract_date ? formatDate(data.contract_date, "PP") : "-"}</span>,
	},
	{
		id: "status",
		label: "Status",
		cellClassName: "",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => {
			const statusColors = {
				active: "bg-green-100 text-green-800",
				inactive: "bg-gray-100 text-gray-800",
				completed: "bg-blue-100 text-blue-800",
			};
			return (
				<span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[data.status]}`}>
					{data.status.charAt(0).toUpperCase() + data.status.slice(1)}
				</span>
			);
		},
	},
];

export const Route = createFileRoute("/_dashboard/projects")({
	validateSearch: z.object({
		"filter[search]": z.string().optional(),
		per_page: z.number().optional(),
		status: z.string().optional(),
		category: z.string().optional(),
		group_by: z.string().optional(),
		page: z.number().min(1).optional(),
	}),
	component: Page,
});

function Page() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const [editingProject, setEditingProject] = useState<Project | null>(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [groupBy, setGroupBy] = useState<string | null>(search.group_by || null);
	const [searchTerm, setSearchTerm] = useState(search["filter[search]"] || "");
	const deleteProjectMutation = useProjectDeleteMutation();

	const projectsQuery = useQuery(
		projectsQueryOptions({
			...search,
			group_by: undefined,
		}),
	);

	const groupedProjectsQuery = useQuery(
		groupedProjectsQueryOptions({
			...search,
			group_by: groupBy,
		}),
	);

	const handleEditProject = (project: Project) => {
		setEditingProject(project);
		setIsEditModalOpen(true);
	};

	const handleDeleteProject = async (project: Project) => {
		if (project.id) {
			await deleteProjectMutation.mutateAsync(project.id.toString());
			await queryClient.invalidateQueries({
				queryKey: ["projects"],
			});
		}
	};

	const handleCloseEditModal = () => {
		setIsEditModalOpen(false);
		setEditingProject(null);
	};

	const handleEditSuccess = () => {
		projectsQuery.refetch();
	};

	const handleCreateProject = () => {
		setIsCreateModalOpen(true);
	};

	const handleCloseCreateModal = () => {
		setIsCreateModalOpen(false);
	};

	const handleCreateSuccess = () => {
		projectsQuery.refetch();
		if (groupBy) {
			groupedProjectsQuery.refetch();
		}
	};

	const handleGroupByChange = async (newGroupBy: string | null) => {
		let groupBy = newGroupBy;
		if (newGroupBy === "client") {
			groupBy = "clientModel";
		}
		setGroupBy(groupBy);
		await navigate({
			to: "/projects",
			search: (old) => ({
				...old,
				group_by: newGroupBy || undefined,
				page: 1,
			}),
		});
	};

	const handleSearch = async () => {
		await navigate({
			to: "/projects",
			search: (old) => ({
				...old,
				"filter[search]": searchTerm || undefined,
				page: 1,
			}),
		});
	};

	const handleClearSearch = async () => {
		setSearchTerm("");
		await navigate({
			to: "/projects",
			search: (old) => ({
				...old,
				"filter[search]": undefined,
				page: 1,
			}),
		});
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			handleSearch();
		}
	};

	const handleExport = async (format: "csv" | "xlsx" | "pdf") => {
		let exportProjects: Project[] = [];

		if (groupBy && groupedProjectsQuery.data?.groups) {
			// Flatten grouped projects for export
			exportProjects = groupedProjectsQuery.data.groups.flatMap((group) => group.projects);
		} else {
			exportProjects = projects;
		}

		if (!exportProjects || exportProjects.length === 0) {
			return;
		}

		const { headers, rows } = prepareProjectDataForExport(exportProjects);
		const filename = `projects_${groupBy ? `grouped_by_${groupBy}_` : ""}${new Date().toISOString().split("T")[0]}`;

		switch (format) {
			case "csv":
				exportToCSV({ headers, rows, filename });
				break;
			case "xlsx":
				exportToXLSX({ headers, rows, filename });
				break;
			case "pdf":
				await exportToPDF({ headers, rows, filename });
				break;
		}
	};

	const projects = projectsQuery.data?.data || [];
	const groupedData = groupedProjectsQuery.data;
	const meta = projectsQuery.data?.meta || {
		current_page: 1,
		last_page: 1,
		from: 1,
		to: 0,
		total: 0,
		per_page: 20,
		status_count: {},
	};

	if (projectsQuery.isLoading || (groupBy && groupedProjectsQuery.isLoading)) {
		return (
			<Content pageName="Projects">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading projects...</p>
					</div>
				</div>
			</Content>
		);
	}

	return (
		<Content pageName="Projects">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-col gap-4">
					<div className="flex flex-row items-center justify-between w-full gap-4">
						<div className="inline-flex flex-col w-auto">
							<span className="text-xl text-foreground-dark font-semibold">Projects</span>
							<span className="text-base text-foreground/70">Manage and monitor your projects</span>
						</div>
						<div className="flex gap-2">
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="outline">
										<IconCategory size={18} stroke={2} />
										<span>Group By</span>
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem onClick={() => handleGroupByChange(null)}>
										<span className={`w-2 h-2 rounded-full mr-2 ${!groupBy ? "bg-blue-500" : "bg-gray-300"}`} />
										No Grouping
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem onClick={() => handleGroupByChange("category")}>
										<IconCategory size={16} className="mr-2" />
										<span className={`w-2 h-2 rounded-full mr-2 ${groupBy === "category" ? "bg-blue-500" : "bg-gray-300"}`} />
										By Category
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>

							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="outline">
										<IconFileExport size={18} stroke={2} />
										<span>Export</span>
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem
										onClick={() => handleExport("csv")}
										disabled={(!projects || projects.length === 0) && (!groupedData?.groups || groupedData.groups.length === 0)}
									>
										<IconFileTypeCsv size={16} className="mr-2" />
										Export as CSV
									</DropdownMenuItem>
									<DropdownMenuItem
										onClick={() => handleExport("xlsx")}
										disabled={(!projects || projects.length === 0) && (!groupedData?.groups || groupedData.groups.length === 0)}
									>
										<IconFileSpreadsheet size={16} className="mr-2" />
										Export as Excel
									</DropdownMenuItem>
									<DropdownMenuItem
										onClick={() => handleExport("pdf")}
										disabled={(!projects || projects.length === 0) && (!groupedData?.groups || groupedData.groups.length === 0)}
									>
										<IconFileTypePdf size={16} className="mr-2" />
										Export as PDF
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
							<Button onClick={handleCreateProject}>
								<IconPlus size={18} stroke={2} />
								<span className="">Add New</span>
							</Button>
						</div>
					</div>

					{/* Search Bar */}
					<div className="flex gap-2">
						<Input
							placeholder="Search by project name, client, or category..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							onKeyPress={handleKeyPress}
							className="flex-1 border"
						/>
						<Button onClick={handleSearch} disabled={!searchTerm.trim()}>
							<IconSearch size={18} stroke={2} />
						</Button>
						{searchTerm && (
							<Button variant="outline" onClick={handleClearSearch}>
								<IconX size={18} stroke={2} />
							</Button>
						)}
					</div>
				</div>
				<div className="flex flex-col w-full gap-4">
					{groupBy && groupedData ? (
						<div className="space-y-6">
							{groupedData.groups.map((group) => (
								<div key={group.group} className="border rounded-lg">
									<div className="bg-gray-50 px-4 py-3 border-b">
										<h3 className="font-semibold text-gray-900">
											{group.group || "Unassigned"} ({group.count} projects)
										</h3>
									</div>
									<div className="p-4">
										<DataTable
											className="border-collapse"
											data={group.projects}
											columns={columns}
											stickyHead={false}
											stickyCol={false}
											showBorder={false}
											compact={true}
											showToolbar={false}
											onEdit={handleEditProject}
											onDelete={(project) => {
												handleDeleteProject(project);
											}}
										/>
									</div>
								</div>
							))}
						</div>
					) : (
						<>
							<DataTable
								className="border-collapse"
								data={projects}
								columns={columns}
								stickyHead={false}
								stickyCol={false}
								showBorder={true}
								compact={true}
								showToolbar={false}
								onEdit={handleEditProject}
								onDelete={(project) => {
									handleDeleteProject(project);
								}}
							/>
							<Pagination
								onClick={async (page) => {
									await navigate({
										to: "/projects",
										search: (old) => ({
											...old,
											page,
										}),
									});
								}}
								initialPage={meta.current_page}
								pageCount={meta.last_page}
							/>
						</>
					)}
				</div>
			</div>
			<CreateProjectModal isOpen={isCreateModalOpen} onClose={handleCloseCreateModal} onSuccess={handleCreateSuccess} />
			{editingProject && <EditProjectModal project={editingProject} isOpen={isEditModalOpen} onClose={handleCloseEditModal} onSuccess={handleEditSuccess} />}
		</Content>
	);
}
